
import { offlineDb } from '@/db/offlineDb';
import { supabase } from '@/integrations/supabase/client';
import { networkService } from '@/services/network.service';
import { syncDataIntegrityService } from './SyncDataIntegrityService';
import OfflineLogger from '@/utils/offlineLogger';
import { getValidUserId } from '@/utils/validation';
import { sanitizeQueueItemCategory } from './sanitizeQueueItemCategory';
import {
  resolveDuplicateProductConflict,
  resolveDuplicateProductPreemptively
} from './productDuplicateResolution';
import { validateQuantity } from '@/utils/security';
import { isNetworkError } from '@/utils/networkError';
import { serializeError } from '@/utils/serializeError';

const DEFAULT_SANITIZED_QUANTITY = (() => {
  const result = validateQuantity('1');
  return result.isValid ? result.sanitized : '1';
})();

const normalizeQuantityValue = (value?: string | number | null): string => {
  const result = validateQuantity(value ?? DEFAULT_SANITIZED_QUANTITY);
  if (result.isValid) {
    return result.sanitized;
  }
  return DEFAULT_SANITIZED_QUANTITY;
};

interface SyncResult {
  success: boolean;
  entitiesSynced: string[];
  recordsProcessed: number;
  errors: string[];
  duration: number;
}

class UnifiedSyncService {
  private readonly ENTITY_MAPPINGS = {
    'categories': 'user_categories',
    'products': 'user_products',
    'shops': 'user_shops',
    'shoppingLists': 'shopping_lists',
    'shoppingItems': 'shopping_items',
    'recurringProducts': 'recurring_products',
    'purchaseHistory': 'shopping_history',
    'images': 'user_products'
  };

  async performUnifiedSync(entityNames?: string[]): Promise<SyncResult> {
    const startTime = Date.now();
    const result: SyncResult = {
      success: true,
      entitiesSynced: [],
      recordsProcessed: 0,
      errors: [],
      duration: 0
    };

    if (!networkService.isOnline()) {
      result.success = false;
      result.errors.push('Cannot sync while offline');
      return result;
    }

    const userId = getValidUserId();
    if (!userId) {
      result.success = false;
      result.errors.push('User ID not found');
      return result;
    }

    const entitiesToSync = entityNames || Object.keys(this.ENTITY_MAPPINGS);

    try {
      OfflineLogger.info('UnifiedSyncService', `Starting unified sync for entities: ${entitiesToSync.join(', ')}`);

      // Phase 1: Process local changes first (pending queue)
      const queueProcessed = await this.processPendingQueue();
      result.recordsProcessed += queueProcessed;

      // Phase 2: Sync each entity with conflict resolution
      for (const entityName of entitiesToSync) {
        try {
          const entityResult = await this.syncEntityWithConflictResolution(entityName, userId);
          result.entitiesSynced.push(entityName);
          result.recordsProcessed += entityResult.recordsProcessed;
          
          if (entityResult.errors.length > 0) {
            result.errors.push(...entityResult.errors);
          }
        } catch (error) {
          const errorMsg = `Failed to sync ${entityName}: ${error}`;
          result.errors.push(errorMsg);
          OfflineLogger.error('UnifiedSyncService.performUnifiedSync', errorMsg);
        }
      }

      // Phase 3: Data integrity validation and cleanup
      for (const entityName of result.entitiesSynced) {
        try {
          await syncDataIntegrityService.validateAndCleanEntityData(entityName, userId);
        } catch (error) {
          OfflineLogger.error('UnifiedSyncService.dataIntegrityCheck', error);
        }
      }

      result.duration = Date.now() - startTime;
      result.success = result.errors.length === 0;

      OfflineLogger.info('UnifiedSyncService', {
        success: result.success,
        entitiesSynced: result.entitiesSynced.length,
        recordsProcessed: result.recordsProcessed,
        errorsCount: result.errors.length,
        durationMs: result.duration
      });

      // Update last sync timestamp
      localStorage.setItem('lastGlobalSync', new Date().toISOString());

      return result;
    } catch (error) {
      result.success = false;
      result.errors.push(`Unified sync failed: ${error}`);
      result.duration = Date.now() - startTime;
      OfflineLogger.error('UnifiedSyncService.performUnifiedSync', error);
      return result;
    }
  }

  private async processPendingQueue(): Promise<number> {
    try {
      const queueItems = await offlineDb.syncQueue.orderBy('timestamp').toArray();
      let processedCount = 0;

      for (const item of queueItems) {
        if (item.status === 'failed') continue;
        if (!networkService.isOnline()) {
          OfflineLogger.warn(
            'UnifiedSyncService.processPendingQueue',
            'Network offline, stopping queue processing'
          );
          break;
        }

        const isValid = await sanitizeQueueItemCategory(item);
        if (!isValid) {
          continue;
        }

        try {
          await this.processSyncQueueItem(item);
          await offlineDb.syncQueue.delete(item.id!);
          processedCount++;
        } catch (error) {
          const detail = serializeError(error);
          OfflineLogger.error(
            'UnifiedSyncService.processPendingQueue',
            `Failed to process queue item ${item.id}: ${detail}`
          );

          // L'item fallito resta in coda (viene rimosso solo in caso di successo):
          // il retry avviene al prossimo ciclo di sync, senza sleep inline che
          // bloccherebbe il processamento degli item successivi della coda.
          const retryCount = (item.retryCount || 0) + 1;
          await offlineDb.syncQueue.update(item.id!, {
            retryCount,
            errorDetail: detail
          });

          if (retryCount >= 3) {
            await offlineDb.syncQueue.update(item.id!, { status: 'failed' });
            OfflineLogger.error(
              'UnifiedSyncService.processPendingQueue',
              `Marking item ${item.id} as failed after ${retryCount} retries — ${detail}`
            );
          } else {
            OfflineLogger.warn(
              'UnifiedSyncService.processPendingQueue',
              `Retry ${retryCount}/3 for item ${item.id} al prossimo ciclo di sync`
            );
          }
        }
      }

      return processedCount;
    } catch (error) {
      OfflineLogger.error('UnifiedSyncService.processPendingQueue', error);
      return 0;
    }
  }

  private async processSyncQueueItem(item: any): Promise<void> {
    if (!networkService.isOnline()) {
      throw new Error('Network offline');
    }
    const isValid = await sanitizeQueueItemCategory(item);
    if (!isValid) {
      throw new Error('Invalid category_id');
    }
    const tableName = this.ENTITY_MAPPINGS[item.entityType as keyof typeof this.ENTITY_MAPPINGS];
    
    if (!tableName) {
      throw new Error(`Unknown entity type: ${item.entityType}`);
    }

    const cleanData = this.cleanDataForSync(item.data);

    switch (item.operation) {
      case 'create':
        try {
          if (item.entityType === 'products') {
            const preemptiveResolution = await resolveDuplicateProductPreemptively({
              cleanData,
              entityId: item.entityId,
              queueItemId: item.id,
              tableName,
              loggerContext: 'UnifiedSyncService.processSyncQueueItem',
              fallbackUserId: cleanData?.user_id || getValidUserId()
            });

            if (preemptiveResolution.resolved) {
              const resolvedId = preemptiveResolution.resolvedProductId ?? item.entityId;
              await this.updateLocalSyncStatus(item.entityType, resolvedId, 'synced');
              OfflineLogger.info(
                'UnifiedSyncService.processSyncQueueItem',
                `Rilevato duplicato per ${item.entityType}:${item.entityId} associandolo al record ${resolvedId} prima dell'upsert`
              );
              return;
            }
          }

          if (['categories', 'products', 'shops', 'shoppingLists', 'recurringProducts'].includes(item.entityType)) {
            const { error } = await supabase
              .from(tableName)
              .upsert([cleanData], { onConflict: 'id' });
            if (error) throw error;
          } else {
            const { error } = await supabase.from(tableName).insert([cleanData]);
            if (error) throw error;
          }
        } catch (error) {
          if (item.entityType === 'products' && (error as { code?: string }).code === '23505') {
            const resolution = await resolveDuplicateProductConflict({
              cleanData,
              entityId: item.entityId,
              queueItemId: item.id,
              tableName,
              loggerContext: 'UnifiedSyncService.processSyncQueueItem',
              fallbackUserId: cleanData?.user_id || getValidUserId()
            });

            if (resolution.resolved) {
              const resolvedId = resolution.resolvedProductId ?? item.entityId;
              await this.updateLocalSyncStatus(item.entityType, resolvedId, 'synced');
              OfflineLogger.info(
                'UnifiedSyncService.processSyncQueueItem',
                `Risolto conflitto di duplicato per ${item.entityType}:${item.entityId} associandolo al record ${resolvedId}`
              );
              return;
            }
          }

          throw error;
        }
        break;

      case 'update':
        const { error: updateError } = await supabase
          .from(tableName)
          .update(cleanData)
          .eq('id', item.entityId);
        if (updateError) throw updateError;
        break;

      case 'delete':
        const { error: deleteError } = await supabase
          .from(tableName)
          .delete()
          .eq('id', item.entityId);
        if (deleteError) throw deleteError;
        break;
    }

    // Update local sync status
    await this.updateLocalSyncStatus(item.entityType, item.entityId, 'synced');
  }

  private async syncEntityWithConflictResolution(entityName: string, userId: string): Promise<{
    recordsProcessed: number;
    errors: string[];
  }> {
    const result = { recordsProcessed: 0, errors: [] };

    try {
      const tableName = this.ENTITY_MAPPINGS[entityName as keyof typeof this.ENTITY_MAPPINGS];
      if (!tableName) {
        throw new Error(`Unknown entity: ${entityName}`);
      }

      // Download from server with user filter
      let query = supabase.from(tableName).select('*');
      
      if (['user_categories', 'user_products', 'user_shops', 'shopping_lists', 'recurring_products'].includes(tableName)) {
        query = query.filter('user_id', 'eq', userId);
      }
      
      const { data: serverData, error } = await query;
      
      if (error) {
        throw error;
      }

      if (serverData && serverData.length > 0) {
        // Clear and rebuild with server data
        const table = offlineDb[entityName];
        if (table) {
          // For user-specific entities, delete only user's data
          if (['categories', 'products', 'shops', 'shoppingLists', 'recurringProducts'].includes(entityName)) {
            await table.where('user_id').equals(userId).delete();
          } else {
            // For shopping items, clear all
            await table.clear();
          }

          // Insert server data with sync metadata
          const dataWithSync = serverData.map(item => {
            const timestampStr =
              item.updated_at || item.created_at || new Date().toISOString();
            return {
              ...item,
              _syncStatus: 'synced' as const,
              _lastModified: new Date(timestampStr).getTime()
            };
          });

          await table.bulkPut(dataWithSync);
          result.recordsProcessed = dataWithSync.length;
        }
      }

      OfflineLogger.info('UnifiedSyncService.syncEntityWithConflictResolution', 
        `Synced ${result.recordsProcessed} records for ${entityName}`);

      return result;
    } catch (error) {
      result.errors.push(`Sync failed for ${entityName}: ${error}`);
      OfflineLogger.error('UnifiedSyncService.syncEntityWithConflictResolution', error);
      return result;
    }
  }

  private cleanDataForSync(data: any): any {
    if (!data) return data;

    const cleanData = { ...data };

    // Remove sync-specific fields
    delete cleanData._syncStatus;
    delete cleanData._lastModified;

    // Convert blank strings to NULL for optional columns
    Object.keys(cleanData).forEach(key => {
      if (cleanData[key] === '') {
        cleanData[key] = null;
      }
    });

    if ('quantity' in cleanData && cleanData.quantity !== null && cleanData.quantity !== undefined) {
      cleanData.quantity = normalizeQuantityValue(cleanData.quantity as string | number | null);
    }

    return cleanData;
  }

  private async updateLocalSyncStatus(entityType: string, entityId: string, status: 'synced' | 'pending' | 'deleted'): Promise<void> {
    try {
      const table = offlineDb[entityType];
      if (table) {
        await table.update(entityId, { _syncStatus: status });
      }
    } catch (error) {
      OfflineLogger.error('UnifiedSyncService.updateLocalSyncStatus', error);
    }
  }

  private async getRemoteMaxUpdatedAt(entityName: string, userId: string): Promise<number | null> {
    try {
      const tableName = this.ENTITY_MAPPINGS[
        entityName as keyof typeof this.ENTITY_MAPPINGS
      ];
      if (!tableName) return null;

      const userFiltered = [
        'user_categories',
        'user_products',
        'user_shops',
        'shopping_lists',
        'shopping_items',
        'recurring_products'
      ].includes(tableName);


      let query = supabase.from(tableName).select('*');

      if (userFiltered) {
        query = query.eq('user_id', userId);
      }

      const { data: updatedData, error: updatedError } = await query
        .select('updated_at')
        .order('updated_at', { ascending: false })
        .limit(1);
      if (updatedError) {
        // Errore di risposta PostgREST (RLS/permessi/query): siamo online,
        // non marcare offline. Si tratta di un problema di query, non di rete.
        OfflineLogger.warn('UnifiedSyncService.getRemoteMaxUpdatedAt', `Query error: ${updatedError.message}`);
        return null;
      }

      let timestamp = updatedData?.[0]?.updated_at as string | null | undefined;

      if (!timestamp) {
        let queryCreated = supabase.from(tableName).select('*');
        if (userFiltered) {
          queryCreated = queryCreated.eq('user_id', userId);
        }
        const { data: createdData, error: createdError } = await queryCreated
          .select('created_at')
          .order('created_at', { ascending: false })
          .limit(1);
        if (createdError) {
          // Vedi sopra: errore di query, non di rete. Non marcare offline.
          OfflineLogger.warn('UnifiedSyncService.getRemoteMaxUpdatedAt', `Query error: ${createdError.message}`);
          return null;
        }
        timestamp = createdData?.[0]?.created_at as string | null | undefined;
      }

      if (!timestamp) return null;
      return new Date(timestamp).getTime();
    } catch (error) {
      // Marca offline solo se è un vero errore di rete (fetch fallito),
      // non per eccezioni applicative che non implicano assenza di connettività.
      if (isNetworkError(error)) {
        networkService.markOffline();
      }
      OfflineLogger.error('UnifiedSyncService.getRemoteMaxUpdatedAt', error);
      return null;
    }
  }

  async isEntitySyncNeeded(entityName: string, userId: string): Promise<boolean> {
    try {
      // Check if there are pending operations
      const pendingOps = await offlineDb.syncQueue
        .where('entityType')
        .equals(entityName)
        .count();
      
      if (pendingOps > 0) {
        return true;
      }

      // Check last modification times
      const table = offlineDb[entityName];
      if (!table) return false;

      const localRecords = await table.where('user_id').equals(userId).toArray();
      if (localRecords.length === 0) return true;

      const localMaxModified = Math.max(
        ...localRecords.map(r => r._lastModified || 0)
      );

      const remoteMax = await this.getRemoteMaxUpdatedAt(entityName, userId);

      OfflineLogger.info('UnifiedSyncService.isEntitySyncNeeded', {
        entityName,
        userId,
        localMaxModified,
        remoteMax
      });

      if (remoteMax !== null) {
        return remoteMax !== localMaxModified;
      }

      const lastSync = localStorage.getItem('lastGlobalSync');

      if (!lastSync) return true;

      const lastSyncTime = new Date(lastSync).getTime();

      return localMaxModified > lastSyncTime;
    } catch (error) {
      OfflineLogger.error('UnifiedSyncService.isEntitySyncNeeded', error);
      return true; // Default to sync needed if check fails
    }
  }
}

export const unifiedSyncService = new UnifiedSyncService();
